import { useMemo } from 'react';
import { useIsOrgAdmin } from '@jira/platform__services-is-org-admin/src/is-org-admin.tsx';
import { PREMIUM_EDITION } from '@jira/platform__application-editions/src/edition.tsx';
import {
	verticalInstantiationResource,
	type VerticalInstantiationsResult,
} from '@jira/router-resources__itam/src/verticalInstantiationResource.tsx';
import { avsWorkspaceResource } from '@jira/router-resources__itam/src/avsWorkspaceResource.tsx';

import { functionWithCondition } from '@jira/platform__utils-feature-flagging-utils/src/utils/function-with-condition/index.tsx';
import { workspaceResource } from '@jira/servicedesk__insight-common-services-workspace-context/src/workspaceResource.tsx';
import { useApplicationPermissions } from '@atlassian/jira-tenant-context-controller/src/components/application-permissions/index.tsx';
import { useAppEditions } from '@atlassian/jira-tenant-context-controller/src/components/app-editions/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useResource } from '@atlassian/react-resource-router';
import { fg } from '@atlassian/jira-feature-gating';
import { useItamPermissionsContext } from './ItamPermissionsContext.tsx';
import type { UserRole } from './UserRole.tsx';
import type {
	HamVerticalInstantiation,
	ItamTenantContextResult,
} from './ItamTenantContextTypes.tsx';
import {
	ADMINISTER_HAM,
	VIEW_HAM,
	SERVICE_DESK_USER,
	JIRA_ADMIN,
	ASSETS_ADMIN,
	HAM_USER,
	HAM_ADMIN,
	type HamPermissions,
} from './HamPermissions.tsx';

const HAM_ADMIN_PERMISSION_ID = 'manage-ham';
const HAM_USER_PERMISSION_ID = 'view-ham';
const SERVICE_DESK_USER_PERMISSION_ID = 'jsd-user';
const JIRA_ADMIN_PERMISSION_ID = 'jira-admin';
const ASSETS_ADMIN_PERMISSION_ID = 'assets-admin';

function deriveRole(isHamAdmin: boolean): UserRole {
	return isHamAdmin ? HAM_ADMIN : HAM_USER;
}

function getHamVerticalInstantiation(
	verticalInstantiations: VerticalInstantiationsResult | undefined,
): HamVerticalInstantiation | null {
	const hamNode = verticalInstantiations?.edges.find((edge) => {
		const node = edge.node;
		return node?.type === 'HAM' && node.id !== '';
	})?.node;
	if (!hamNode || hamNode.type !== 'HAM' || hamNode.id === '') {
		return null;
	}

	const categories = (hamNode.categories?.edges ?? []).reduce<
		Array<{ readonly id: string; readonly category: string; readonly status?: string | null }>
	>((accumulator, categoryEdge) => {
		const categoryNode = categoryEdge.node;
		if (categoryNode?.id && categoryNode.category) {
			accumulator.push(categoryNode);
		}
		return accumulator;
	}, []);

	return {
		id: hamNode.id,
		status: hamNode.status,
		canRetry: hamNode.canRetry,
		categories,
	};
}

/**
 * Hook that exposes JSM permissions, required license (e.g. JSM Premium for HAM; can support Jira later),
 * HAM role, and derived access for ITAM pages.
 * Access is granted only when user has the required app permission, tenant has the required license,
 * and the HAM permission check grants view access.
 */
export function useItamTenantContextMain(): ItamTenantContextResult {
	const appPermissions = useApplicationPermissions();
	const appEditions = useAppEditions();
	const { isOrgAdmin, loading: isLoadingOrgAdmin } = useIsOrgAdmin();
	const { data: workspaceId } = useResource(workspaceResource);
	const { data: verticalInstantiations } = useResource(verticalInstantiationResource);
	const { data: hamPermissionChecks, loading: isLoadingHamPermissionChecks } =
		useItamPermissionsContext();
	const { data: avsWorkspaceData } = useResource(avsWorkspaceResource);
	// The resource resolves `string | null`; the tenant context intentionally exposes
	// `string | undefined`, so coerce a `null` result to `undefined`.
	const avsWorkspaceId = avsWorkspaceData ?? undefined;
	const cloudId = useCloudId();

	return useMemo(() => {
		const isLoading =
			appPermissions == null ||
			appEditions == null ||
			isLoadingOrgAdmin ||
			isLoadingHamPermissionChecks;

		// must have premium edition and service desk access
		const hasRequiredLicense = appEditions?.serviceDesk === PREMIUM_EDITION;
		const hasServiceDeskAccess = !!appPermissions?.hasServiceDeskAccess;
		const hasHamAdminPermission = !!hamPermissionChecks?.permissions[HAM_ADMIN_PERMISSION_ID];
		const hasHamUserPermission = !!hamPermissionChecks?.permissions[HAM_USER_PERMISSION_ID];
		const hasJiraServiceDeskUserPermission =
			!!hamPermissionChecks?.permissions[SERVICE_DESK_USER_PERMISSION_ID];
		const hasJiraAdminPermission = !!hamPermissionChecks?.permissions[JIRA_ADMIN_PERMISSION_ID];
		const hasAssetsAdminPermission = !!hamPermissionChecks?.permissions[ASSETS_ADMIN_PERMISSION_ID];

		const hasAccess =
			!isLoading && hasServiceDeskAccess && hasRequiredLicense && hasJiraServiceDeskUserPermission;

		const isAdmin = hasAccess && (hasHamAdminPermission || hasJiraAdminPermission);
		const role = deriveRole(isAdmin);
		const hasUserAccess = hasAccess && (hasHamUserPermission || hasJiraAdminPermission);

		const permissions: HamPermissions = {
			// Bypass HAM roles if user is jira admin as mentioned in the doc https://hello.atlassian.net/wiki/x/PTKVegE
			// We will deprecate ADMINISTER_HAM and VIEW_HAM permission in the future as we are following PRODUCT_ROLE naming convention
			[ADMINISTER_HAM]: isAdmin,
			[VIEW_HAM]: hasUserAccess,
			[HAM_USER]: hasUserAccess,
			[HAM_ADMIN]: isAdmin,
			[SERVICE_DESK_USER]: hasJiraServiceDeskUserPermission,
			[JIRA_ADMIN]: hasJiraAdminPermission,
			[ASSETS_ADMIN]: hasAssetsAdminPermission,
		};
		const shouldShowItam = !isLoading && hasAccess;
		const hamVerticalInstantiation = getHamVerticalInstantiation(verticalInstantiations);
		return {
			ham: {
				hasRequiredLicense,
				hasAccess,
				isUser: hasUserAccess,
				isAdmin,
				role,
				isLoading,
				permissions,
				// We will deperecate {shouldShowItam} prop in the future
				shouldShowItam,
				verticalInstantiation: hamVerticalInstantiation,
			},
			isOrgAdmin,
			isLoading: isLoadingOrgAdmin,
			workspaceId,
			avsWorkspaceId,
			cloudId,
			// TODO : we will remove this once we integarte the ham.verticalInstantiation.
			verticalInstantiationId: hamVerticalInstantiation?.id ?? null,
			verticalInstantiationStatus: hamVerticalInstantiation?.status ?? null,
		};
	}, [
		appPermissions,
		appEditions,
		isOrgAdmin,
		isLoadingOrgAdmin,
		isLoadingHamPermissionChecks,
		hamPermissionChecks,
		verticalInstantiations,
		workspaceId,
		avsWorkspaceId,
		cloudId,
	]);
}

const useItamTenantContextDefault = (): ItamTenantContextResult => ({
	ham: {
		shouldShowItam: false,
		hasRequiredLicense: false,
		hasAccess: false,
		isUser: false,
		isAdmin: false,
		role: HAM_ADMIN,
		isLoading: false,
		permissions: {
			VIEW_HAM: false,
			ADMINISTER_HAM: false,
			[SERVICE_DESK_USER]: false,
			[JIRA_ADMIN]: false,
			[ASSETS_ADMIN]: false,
			[HAM_ADMIN]: false,
			[HAM_USER]: false,
		},
		verticalInstantiation: null,
	},
	isLoading: false,
	isOrgAdmin: false,
	workspaceId: null,
	avsWorkspaceId: undefined,
	cloudId: null,
	verticalInstantiationId: null,
	verticalInstantiationStatus: null,
});

export const useItamTenantContext: () => ItamTenantContextResult = functionWithCondition(
	() => fg('itam_ftw'),
	useItamTenantContextMain,
	// Returns default values just to satisfy the hooks `readonly` types.
	useItamTenantContextDefault,
);
